import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Stack, Switch } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { CONFIRMATION_DIALOG_TYPE_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  ConfirmationDialogMetadata,
  ConfirmationDialogSettingItem,
  ConfirmationDialogThresholdType,
  ConfirmationDialogType,
} from 'src/WebApiController';

import * as styles from './DialogSuppressionFormContent.css';

interface DialogSuppressionFormContentProps {
  metadata: ConfirmationDialogMetadata[];
}

export const DialogSuppressionFormContent = ({
  metadata,
}: DialogSuppressionFormContentProps) => {
  const { watch, setValue } = useFormContext();
  const watchedSettings = watch();

  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();

  const uiCurrency = useMemo(
    () => getUiCurrency(loginContext?.user?.activeAccount?.currencyCode),
    [getUiCurrency, loginContext?.user?.activeAccount?.currencyCode]
  );

  const thresholdString = useContent(ContentId.Threshold);

  const renderThresholdInput = (
    dialogType: ConfirmationDialogType,
    thresholdType: ConfirmationDialogThresholdType,
    setting: ConfirmationDialogSettingItem
  ) => {
    if (thresholdType === ConfirmationDialogThresholdType.None) {
      return null;
    }

    const isAbsolute =
      thresholdType === ConfirmationDialogThresholdType.Absolute;
    const fieldName = isAbsolute ? 'absoluteThreshold' : 'relativeThreshold';
    const value = setting[fieldName];

    return (
      <div className={styles.formFieldStyle}>
        <PosFormField>
          <PosTextField
            type="number"
            prefixDisplay={
              isAbsolute ? <span>{uiCurrency.sym}</span> : undefined
            }
            postfixDisplay={!isAbsolute ? <span>%</span> : undefined}
            placeholder={thresholdString}
            value={value != null ? value.toString() : ''}
            onChange={(e) => {
              const inputValue = e.target.value;
              setValue(
                `${dialogType}.${fieldName}`,
                inputValue === '' ? null : parseFloat(inputValue),
                { shouldDirty: true }
              );
            }}
            disabled={!setting.isActive}
          />
        </PosFormField>
      </div>
    );
  };

  const renderSettingRow = (meta: ConfirmationDialogMetadata) => {
    const setting = watchedSettings[meta.dialogType];
    const contentIds = CONFIRMATION_DIALOG_TYPE_TO_CID[meta.dialogType];

    if (!contentIds || !setting) {
      return null;
    }

    const detailContent = (
      <Switch
        checked={setting.isActive}
        onCheckedChange={(checked) =>
          setValue(`${meta.dialogType}.isActive`, checked, {
            shouldDirty: true,
          })
        }
      />
    );

    const thresholdInput = renderThresholdInput(
      meta.dialogType,
      meta.thresholdType,
      setting
    );

    return (
      <div key={meta.dialogType} className={styles.settingRow}>
        <div className={styles.settingRowHeader}>
          <div className={styles.settingTitle}>
            <Content id={contentIds.title} />
          </div>
          <div className={styles.settingDescription}>
            <Content id={contentIds.description} />
          </div>
        </div>
        <div className={styles.settingRowDetail}>
          {thresholdInput ? (
            <Stack
              className={styles.settingDetail}
              direction="row"
              gap="xl"
              alignItems="center"
            >
              {detailContent}
              {thresholdInput}
            </Stack>
          ) : (
            detailContent
          )}
        </div>
      </div>
    );
  };

  return (
    <Stack direction="column" gap="xxl">
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Content id={ContentId.PricingDialogs} />
        </div>
        <Stack direction="column" gap="xl">
          {metadata.map((meta) => renderSettingRow(meta))}
        </Stack>
      </div>
    </Stack>
  );
};
